import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Camera, X, Trash2, ZoomIn, Pencil, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ImageAttachment } from '../api'
import { getEntryImages, uploadAndAttachImage, detachImage } from '../api'
import { apiRequest, invalidateCache } from '../../../shared/api/base'

interface ImageGalleryProps {
  entryId: string
  /** Whether the entry is a temp note (not yet saved to server) */
  isTemp?: boolean
}

export default function ImageGallery({ entryId, isTemp }: ImageGalleryProps) {
  const { t } = useTranslation('notebook')
  const [images, setImages] = useState<ImageAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [editingCaption, setEditingCaption] = useState<string | null>(null)
  const [captionValue, setCaptionValue] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadImages = useCallback(async () => {
    if (isTemp) return
    try {
      const result = await getEntryImages(entryId)
      setImages(result)
    } catch (err) {
      console.error('Failed to load images:', err)
    }
  }, [entryId, isTemp])

  useEffect(() => { loadImages() }, [loadImages])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const attachment = await uploadAndAttachImage(file, entryId)
        setImages(prev => [...prev, attachment])
      }
    } catch (err) {
      console.error('Failed to upload image:', err)
    } finally {
      setUploading(false)
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [entryId])

  const handleDetach = useCallback(async (imageId: string) => {
    const prev = images
    setImages(imgs => imgs.filter(i => i.id !== imageId))
    // Close lightbox if showing the deleted image
    setLightboxIndex(null)
    try {
      await detachImage(imageId, entryId)
    } catch (err) {
      console.error('Failed to detach image:', err)
      setImages(prev)
    }
  }, [images, entryId])

  const handleCaptionSave = useCallback(async (imageId: string) => {
    const newCaption = captionValue.trim() || null
    setImages(imgs =>
      imgs.map(i => i.id === imageId ? { ...i, caption: newCaption } : i)
    )
    setEditingCaption(null)
    try {
      invalidateCache(`/images/notebook/${entryId}`)
      await apiRequest(`/images/${imageId}`, {
        method: 'PATCH',
        body: JSON.stringify({ caption: newCaption }),
      })
    } catch (err) {
      console.error('Failed to update caption:', err)
    }
  }, [captionValue, entryId])

  const lightboxImage = lightboxIndex !== null ? images[lightboxIndex] : null

  return (
    <div className="mt-4">
      {/* Header + Add Photo button */}
      <div className="flex items-center justify-between mb-3">
        {images.length > 0 && (
          <span className="text-xs text-pearl/60 uppercase tracking-wider font-medium">
            {t('photos.title')} ({images.length})
          </span>
        )}
        <div className="ml-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            multiple
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isTemp || uploading}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isTemp || uploading}
            className="flex items-center gap-1.5 text-xs text-gold/70 hover:text-gold transition-colors disabled:opacity-30"
          >
            <Camera size={14} />
            {t('photos.addPhoto')}
          </button>
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-gold/5 border border-gold/10 rounded-lg">
          <span className="inline-block w-3 h-3 border-2 border-gold/40 border-t-gold rounded-full animate-spin" />
          <span className="text-xs text-pearl/60">{t('photos.uploading')}</span>
        </div>
      )}

      {/* Image grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((img, index) => (
            <motion.button
              key={img.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-square rounded-lg overflow-hidden bg-charcoal border border-gold/10 group"
              onClick={() => setLightboxIndex(index)}
            >
              <img
                src={img.thumbnailUrl || img.url}
                alt={img.caption || img.filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn size={20} className="text-white/0 group-hover:text-white/80 transition-colors" />
              </div>
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1">
                  <span className="text-[10px] text-white/80 line-clamp-1">{img.caption}</span>
                </div>
              )}
            </motion.button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
            onClick={() => setLightboxIndex(null)}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 bg-black/50" onClick={e => e.stopPropagation()}>
              <span className="text-sm text-pearl/60">
                {lightboxIndex! + 1} / {images.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDetach(lightboxImage.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Trash2 size={16} className="text-rose/70" />
                </button>
                <button
                  onClick={() => setLightboxIndex(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X size={18} className="text-pearl/60" />
                </button>
              </div>
            </div>

            {/* Image */}
            <div
              className="flex-1 flex items-center justify-center overflow-auto p-4"
              onClick={() => setLightboxIndex(null)}
            >
              <img
                src={lightboxImage.url}
                alt={lightboxImage.caption || lightboxImage.filename}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={e => e.stopPropagation()}
                style={{ touchAction: 'pinch-zoom' }}
              />
            </div>

            {/* Caption bar */}
            <div className="px-4 py-3 bg-black/50" onClick={e => e.stopPropagation()}>
              {editingCaption === lightboxImage.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={captionValue}
                    onChange={e => setCaptionValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCaptionSave(lightboxImage.id) }}
                    placeholder={t('photos.captionPlaceholder')}
                    autoFocus
                    className="flex-1 bg-transparent text-sm text-pearl/80 placeholder:text-pearl/55 focus:outline-none border-b border-gold/20 pb-1"
                  />
                  <button
                    onClick={() => handleCaptionSave(lightboxImage.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors"
                  >
                    <Check size={14} className="text-gold" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setEditingCaption(lightboxImage.id)
                    setCaptionValue(lightboxImage.caption || '')
                  }}
                  className="flex items-center gap-2 text-sm text-pearl/50 hover:text-pearl/70 transition-colors"
                >
                  <Pencil size={12} />
                  {lightboxImage.caption || t('photos.captionPlaceholder')}
                </button>
              )}
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                {lightboxIndex! > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i !== null ? i - 1 : null) }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white/70 transition-colors"
                  >
                    ‹
                  </button>
                )}
                {lightboxIndex! < images.length - 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i !== null ? i + 1 : null) }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white/70 transition-colors"
                  >
                    ›
                  </button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
